<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-bed me-2"></i>{{ $t('page.admissions', 'Admissions') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="$router.push('/wards')">
            <i class="mdi mdi-hospital-building me-1"></i>Wards & Beds
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterStatus='active'" :class="{ selected: filterStatus==='active' }">
          <div class="hms-stat-num hms-stat-info">{{ activeCount }}</div>
          <div class="hms-stat-lbl">Active</div>
        </div>
        <div class="hms-stat" @click="filterStatus='discharged'" :class="{ selected: filterStatus==='discharged' }">
          <div class="hms-stat-num hms-stat-success">{{ dischargedCount }}</div>
          <div class="hms-stat-lbl">Discharged</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ todayAdmissions }}</div>
          <div class="hms-stat-lbl">Admitted Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ todayDischarges }}</div>
          <div class="hms-stat-lbl">Discharged Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ avgLos.toFixed(1) }}</div>
          <div class="hms-stat-lbl">Avg LOS (days)</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: filterStatus==='active' }"     @click="filterStatus='active'">Active</button>
        <button class="hms-tab" :class="{ active: filterStatus==='discharged' }" @click="filterStatus='discharged'">Discharged</button>
        <button class="hms-tab" :class="{ active: filterStatus==='' }"           @click="filterStatus=''">All</button>
      </div>

      <div v-if="loading && !admissions.length" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-if="!loading && pending.length" class="hms-card hms-pending-card mb-3">
        <div class="hms-section-label mb-2"><i class="mdi mdi-bed-clock me-1"></i>PENDING WARD ASSIGNMENT ({{ pending.length }}) — dispositioned "admit" by clinician, no bed yet</div>
        <div style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Patient</th><th>Visit</th><th>Dispositioned</th><th>Doctor</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              <tr v-for="pv in pending" :key="pv.visit_id">
                <td>
                  <div class="hms-text fw-semibold">{{ pv.patient_name }}</div>
                  <div class="hms-muted-sm">{{ pv.patient_no }} · {{ ageStr(pv.dob) }} · {{ pv.gender }}</div>
                </td>
                <td><span class="hms-mono">{{ pv.visit_number }}</span></td>
                <td>{{ formatDateTime(pv.disposition_time) }}</td>
                <td>{{ pv.doctor_name || '—' }}</td>
                <td><div class="hms-clip">{{ pv.disposition_notes || pv.chief_complaint || '—' }}</div></td>
                <td>
                  <button class="hms-btn hms-btn-sm hms-btn-primary" @click="$router.push('/wards')" title="Assign a bed in Wards & Beds">
                    <i class="mdi mdi-bed me-1"></i>Assign bed
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!loading || admissions.length" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search patient, admission #, doctor..." />
          </div>
          <select class="hms-input hms-filter" v-model="filterWard">
            <option value="">All wards</option>
            <option v-for="w in wards" :key="w" :value="w">{{ w }}</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredList.length }} of {{ admissions.length }}</span>
        </div>

        <div v-if="filteredList.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Admission #</th>
              <th>Patient</th>
              <th>Ward / Bed</th>
              <th>Admitted</th>
              <th>LOS</th>
              <th>Doctor</th>
              <th>Diagnosis</th>
              <th>Status</th>
              <th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="a in filteredList" :key="a.admission_id">
                <td><span class="hms-mono">{{ a.admission_number }}</span><div class="hms-muted-sm">{{ a.admission_type }}</div></td>
                <td>
                  <div class="hms-text fw-semibold">{{ a.patient_name }}</div>
                  <div class="hms-muted-sm">{{ a.patient_no }} · {{ ageStr(a.dob) }} · {{ a.gender }}</div>
                </td>
                <td>
                  <div>{{ a.ward_name || '—' }}</div>
                  <div class="hms-muted-sm"><i class="mdi mdi-bed"></i> {{ a.bed_number || '—' }}</div>
                </td>
                <td>
                  <div>{{ formatDate(a.admission_date) }}</div>
                  <div class="hms-muted-sm">Exp: {{ formatDate(a.expected_discharge_date) || '—' }}</div>
                </td>
                <td>
                  <strong :class="losClass(a)">{{ losDays(a) }}d</strong>
                  <div class="hms-muted-sm" v-if="losOverdue(a)">overdue</div>
                </td>
                <td>{{ a.doctor_name || '—' }}</td>
                <td><div class="hms-clip">{{ a.diagnosis_on_admission || '—' }}</div></td>
                <td><span class="hms-badge" :class="statusBadge(a.status)">{{ a.status }}</span></td>
                <td class="d-flex gap-1 flex-wrap">
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openDetail(a)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button v-if="a.status === 'discharged'" class="hms-btn hms-btn-sm hms-btn-blue" @click="printDischarge(a.admission_id)" title="Print discharge summary"><i class="mdi mdi-printer"></i></button>
                  <button v-if="a.status === 'active'" class="hms-btn hms-btn-sm hms-btn-amber" @click="openTransfer(a)" title="Transfer"><i class="mdi mdi-swap-horizontal"></i></button>
                  <button v-if="a.status === 'active'" class="hms-btn hms-btn-sm hms-btn-success" @click="openDischarge(a)" title="Discharge"><i class="mdi mdi-home-export-outline"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-bed-empty"></i>No admissions match.</div>
      </div>

      <!-- DETAIL MODAL -->
      <div class="hms-modal-overlay" v-if="showDetail && active" @click.self="showDetail=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bed me-1"></i>{{ active.admission_number }} · {{ active.patient_name }}</span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span>{{ active.patient_name }} <span class="hms-muted-sm">· {{ active.patient_no }} · {{ ageStr(active.dob) }} · {{ active.gender }}</span></span></div>
            <div><span class="hms-kv-key">Type</span><span>{{ active.admission_type }}</span></div>
            <div><span class="hms-kv-key">Ward / Bed</span><span>{{ active.ward_name || '—' }} · {{ active.bed_number || '—' }}</span></div>
            <div><span class="hms-kv-key">Admitted</span><span>{{ formatDateTime(active.admission_date) }} <span class="hms-muted-sm">({{ losDays(active) }} days)</span></span></div>
            <div v-if="active.expected_discharge_date"><span class="hms-kv-key">Expected Discharge</span><span>{{ formatDate(active.expected_discharge_date) }}</span></div>
            <div v-if="active.actual_discharge_date"><span class="hms-kv-key">Actual Discharge</span><span>{{ formatDateTime(active.actual_discharge_date) }}</span></div>
            <div><span class="hms-kv-key">Doctor</span><span>{{ active.doctor_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Admission Dx</span><span>{{ active.diagnosis_on_admission || '—' }}</span></div>
            <div v-if="active.discharge_diagnosis"><span class="hms-kv-key">Discharge Dx</span><span>{{ active.discharge_diagnosis }}</span></div>
            <div v-if="active.discharge_condition"><span class="hms-kv-key">Condition</span><span>{{ active.discharge_condition }}</span></div>
            <div v-if="active.discharge_summary"><span class="hms-kv-key">Summary</span><span class="hms-pre">{{ active.discharge_summary }}</span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="statusBadge(active.status)">{{ active.status }}</span></span></div>
          </div>
          <div class="d-flex gap-2 flex-wrap" v-if="active.status === 'active'">
            <button class="hms-btn hms-btn-blue" @click="$router.push('/consultation?visit=' + active.visit_id)" v-if="active.visit_id">
              <i class="mdi mdi-doctor me-1"></i>Consultation
            </button>
            <button class="hms-btn hms-btn-amber" @click="showDetail=false; openTransfer(active)">
              <i class="mdi mdi-swap-horizontal me-1"></i>Transfer
            </button>
            <button class="hms-btn hms-btn-success" @click="showDetail=false; openDischarge(active)">
              <i class="mdi mdi-home-export-outline me-1"></i>Discharge
            </button>
          </div>
          <div class="d-flex gap-2 flex-wrap mt-2" v-if="active.status === 'discharged'">
            <button class="hms-btn hms-btn-blue ms-auto" @click="printDischarge(active.admission_id)">
              <i class="mdi mdi-printer me-1"></i>Print Discharge Summary
            </button>
          </div>
        </div>
      </div>

      <!-- DISCHARGE WIZARD -->
      <div class="hms-modal-overlay" v-if="showDischarge && active" @click.self="showDischarge=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-home-export-outline me-1"></i>Discharge: {{ active.patient_name }}</span>
            <button class="hms-modal-close" @click="showDischarge=false">&times;</button>
          </div>
          <div v-if="dischargeError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ dischargeError }}</div>

          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            On discharge, the bed (<strong>{{ active.ward_name }} · {{ active.bed_number }}</strong>) will be flagged for cleaning.
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Discharge Condition *</label>
              <select class="hms-input" v-model="discharge.discharge_condition">
                <option value="improved">Improved</option>
                <option value="stable">Stable</option>
                <option value="recovered">Recovered</option>
                <option value="unchanged">Unchanged</option>
                <option value="deteriorated">Deteriorated</option>
                <option value="deceased">Deceased</option>
                <option value="lama">Left Against Medical Advice</option>
                <option value="absconded">Absconded</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">LOS</label>
              <input class="hms-input" :value="losDays(active) + ' days'" disabled />
            </div>
            <div class="col-12"><label class="hms-label">Discharge Diagnosis *</label>
              <input class="hms-input" v-model="discharge.discharge_diagnosis" placeholder="Final diagnosis at discharge" />
            </div>
            <div class="col-12"><label class="hms-label">Discharge Summary</label>
              <textarea class="hms-input" rows="6" v-model="discharge.discharge_summary"
                        placeholder="Reason for admission, course in hospital, treatments given, condition at discharge, follow-up plan, medications continued..."></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="confirmDischarge" :disabled="processing || !discharge.discharge_diagnosis">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Discharging...' : 'Confirm Discharge' }}
          </button>
        </div>
      </div>

      <!-- TRANSFER MODAL -->
      <div class="hms-modal-overlay" v-if="showTransfer && active" @click.self="showTransfer=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-swap-horizontal me-1"></i>Transfer: {{ active.patient_name }}</span>
            <button class="hms-modal-close" @click="showTransfer=false">&times;</button>
          </div>
          <div v-if="transferError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ transferError }}</div>

          <div class="hms-muted-sm mb-2">From: <strong>{{ active.ward_name }} · {{ active.bed_number }}</strong></div>

          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">To Bed *</label>
              <select class="hms-input" v-model="transfer.to_bed_id">
                <option value="">— Select an available bed —</option>
                <option v-for="b in availableBeds" :key="b.bed_id" :value="b.bed_id">
                  {{ b.ward_name }} · {{ b.bed_number }} ({{ b.bed_type }})
                </option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Reason *</label>
              <textarea class="hms-input" rows="3" v-model="transfer.transfer_reason" placeholder="e.g. Step-down to general ward; Isolation needed; Closer to monitoring..."></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="confirmTransfer" :disabled="processing || !transfer.to_bed_id || !transfer.transfer_reason">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Transferring...' : 'Confirm Transfer' }}
          </button>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-text { color: var(--hms-h); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-clip { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hms-section-label { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.hms-pending-card { border-left: 4px solid var(--hms-warning, #ffc107); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-info { color: var(--hms-info); }
.hms-stat-success { color: var(--hms-success); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-m); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: rgba(40,167,69,.25); }

.text-overdue { color: var(--hms-danger) !important; }
.text-warn { color: var(--hms-warning) !important; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 160px; font-weight: 600; }
.hms-pre { white-space: pre-wrap; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "AdmissionsManagement",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      admissions: [], allBeds: [], pending: [],

      filterStatus: 'active', search: '', filterWard: '',

      active: null, showDetail: false,

      showDischarge: false, dischargeError: '',
      discharge: { discharge_diagnosis: '', discharge_summary: '', discharge_condition: 'improved' },

      showTransfer: false, transferError: '',
      transfer: { to_bed_id: '', transfer_reason: '' },

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    activeCount()      { return this.admissions.filter(a => a.status === 'active').length; },
    dischargedCount()  { return this.admissions.filter(a => a.status === 'discharged').length; },
    todayAdmissions()  {
      var today = new Date().toISOString().slice(0,10);
      return this.admissions.filter(a => (a.admission_date || '').slice(0,10) === today).length;
    },
    todayDischarges()  {
      var today = new Date().toISOString().slice(0,10);
      return this.admissions.filter(a => (a.actual_discharge_date || '').slice(0,10) === today).length;
    },
    avgLos() {
      var done = this.admissions.filter(a => a.status === 'discharged' && a.admission_date && a.actual_discharge_date);
      if (!done.length) return 0;
      var total = done.reduce((s,a) => {
        var d1 = new Date(a.admission_date.replace(' ','T'));
        var d2 = new Date(a.actual_discharge_date.replace(' ','T'));
        return s + ((d2 - d1) / (24 * 3600 * 1000));
      }, 0);
      return total / done.length;
    },
    wards() {
      var s = new Set();
      this.admissions.forEach(a => { if (a.ward_name) s.add(a.ward_name); });
      return Array.from(s).sort();
    },
    filteredList() {
      var list = this.admissions;
      if (this.filterStatus) list = list.filter(a => a.status === this.filterStatus);
      if (this.filterWard)   list = list.filter(a => a.ward_name === this.filterWard);
      var q = (this.search || '').trim().toLowerCase();
      if (q) {
        list = list.filter(a => ((a.admission_number||'') + ' ' + (a.patient_name||'') + ' ' + (a.patient_no||'') + ' ' + (a.doctor_name||'') + ' ' + (a.diagnosis_on_admission||'')).toLowerCase().includes(q));
      }
      return list;
    },
    availableBeds() {
      return (this.allBeds || []).filter(b => b.status === 'available' && b.bed_id !== (this.active && this.active.bed_id));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    printDischarge(id) { window.open(this.$router.resolve({ name: 'print', params: { type: 'discharge', id }, query: { auto: '1' } }).href, '_blank'); },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    ageStr(dob) { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return '?'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    formatDate(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleString(); },
    losDays(a) {
      if (!a.admission_date) return 0;
      var start = new Date(a.admission_date.replace(' ','T'));
      var end   = a.actual_discharge_date ? new Date(a.actual_discharge_date.replace(' ','T')) : new Date();
      return Math.max(0, Math.floor((end - start) / (24 * 3600 * 1000)));
    },
    losOverdue(a) {
      if (a.status !== 'active' || !a.expected_discharge_date) return false;
      return new Date() > new Date(a.expected_discharge_date.replace(' ','T'));
    },
    losClass(a) { return this.losOverdue(a) ? 'text-overdue' : ''; },
    statusBadge(s) { return ({ active:'hms-badge-info', discharged:'hms-badge-success', cancelled:'hms-badge-neutral', deceased:'hms-badge-warning' })[s] || 'hms-badge-neutral'; },

    async loadAll() {
      this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({ function: "getAdmissions", status: "" }, hms),
          executeJson({ function: "getBeds" }, hms),
          executeJson({ function: "getPendingAdmissions" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.admissions = (res[0].data.data && res[0].data.data.Admissions) || [];
        if (res[1].data && res[1].data.success) this.allBeds    = (res[1].data.data && res[1].data.data.Beds) || [];
        if (res[2].data && res[2].data.success) this.pending    = (res[2].data.data && res[2].data.data.Pending) || [];
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    openDetail(a) { this.active = a; this.showDetail = true; },

    openDischarge(a) {
      this.active = a;
      this.discharge = { discharge_diagnosis: a.diagnosis_on_admission || '', discharge_summary: '', discharge_condition: 'improved' };
      this.dischargeError = '';
      this.showDischarge = true;
    },
    async confirmDischarge() {
      this.dischargeError = '';
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({
          function: "dischargePatient",
          admission_id: this.active.admission_id,
          bed_id: this.active.bed_id
        }, this.discharge), hms);
        if (r.data && r.data.success) {
          this.showToast("Discharged · Bed flagged for cleaning");
          this.showDischarge = false;
          await this.loadAll();
        } else { this.dischargeError = r.data.message || "Failed."; }
      } catch(e) { this.dischargeError = "Connection error."; }
      this.processing = false;
    },

    openTransfer(a) {
      this.active = a;
      this.transfer = { to_bed_id: '', transfer_reason: '' };
      this.transferError = '';
      this.showTransfer = true;
    },
    async confirmTransfer() {
      this.transferError = '';
      this.processing = true;
      try {
        var r = await executeJson({
          function: "transferPatient",
          admission_id: this.active.admission_id,
          patient_id: this.active.patient_id,
          from_bed_id: this.active.bed_id,
          to_bed_id: this.transfer.to_bed_id,
          transfer_reason: this.transfer.transfer_reason
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Transfer recorded");
          this.showTransfer = false;
          await this.loadAll();
        } else { this.transferError = r.data.message || "Failed."; }
      } catch(e) { this.transferError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
